/**
 * Changelog — Versionshistorie der Anwendung mit Typ-Filterung.
 *
 * Features:
 * - Filterung nach Typ: Feature, Bugfix, Verbesserung, Breaking
 * - Chronologische Darstellung gruppiert nach Version
 * - Farbcodierte Typ-Badges
 *
 * @page /changelog
 * @roles alle (authentifiziert)
 * @i18n changelog, common
 * @endpoints GET /api/changelog
 */
import { useState, useEffect } from 'react';
import { Sparkles, Bug, Wrench, AlertTriangle } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const API = import.meta.env.VITE_API_URL || '';

const TYP_CONFIG = {
  feature:     { Icon: Sparkles,        label: 'Feature',       color: 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400' },
  bugfix:      { Icon: Bug,             label: 'Bugfix',        color: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400' },
  improvement: { Icon: Wrench,          label: 'Verbesserung',  color: 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400' },
  breaking:    { Icon: AlertTriangle,   label: 'Breaking',       color: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400' },
};

export default function Changelog() {
  const { t, language } = useLanguage();
  const [versions, setVersions] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [seite, setSeite] = useState(1);
  const proSeite = 5;

  useEffect(() => {
    const params = filter !== 'all' ? `?typ=${filter}` : '';
    fetch(`${API}/api/changelog${params}`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => { setVersions(d.versions || []); setSeite(1); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [filter]);

  const formatDate = (d) => {
    if (!d) return '';
    const locale = language === 'de' ? 'de-AT' : 'en-US';
    const opts = { day: '2-digit', month: 'long', year: 'numeric' };
    // DD.MM.YYYY numeric format (e.g. "07.04.2026")
    const numMatch = d.trim().match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
    if (numMatch) {
      const date = new Date(Number(numMatch[3]), Number(numMatch[2]) - 1, Number(numMatch[1]));
      if (!isNaN(date.getTime())) return date.toLocaleDateString(locale, opts);
    }
    // "DD. MonthName YYYY" format (e.g. "07. April 2026")
    const monate = {
      'januar': 0, 'jaenner': 0, 'jänner': 0, 'februar': 1,
      'maerz': 2, 'märz': 2, 'april': 3, 'mai': 4, 'juni': 5,
      'juli': 6, 'august': 7, 'september': 8, 'oktober': 9,
      'november': 10, 'dezember': 11,
    };
    const m = d.trim().match(/(\d{1,2})\.\s*(\w+)\s+(\d{4})/);
    if (m) {
      const monat = monate[m[2].toLowerCase()];
      if (monat !== undefined) {
        const date = new Date(Number(m[3]), monat, Number(m[1]));
        if (!isNaN(date.getTime())) return date.toLocaleDateString(locale, opts);
      }
    }
    return d;
  };

  const filters = [
    { key: 'all', label: t('common.all') },
    { key: 'feature', label: 'Features', Icon: Sparkles },
    { key: 'bugfix', label: 'Bugfixes', Icon: Bug },
    { key: 'improvement', label: 'Verbesserungen', Icon: Wrench },
  ];

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-xl font-semibold text-content">Changelog</h1>
        <p className="text-[var(--text-secondary)] mt-1">{t('changelog.subtitle')}</p>
      </div>

      {/* Filter */}
      <div className="flex flex-wrap gap-2 mb-8">
        {filters.map(f => (
          <button
            key={f.key}
            onClick={() => { setFilter(f.key); setLoading(true); }}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              filter === f.key
                ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400'
                : 'bg-surface-elevated text-[var(--text-tertiary)] hover:text-content hover:bg-surface-hover'
            }`}
          >
            <span className="flex items-center gap-1">{f.Icon && <f.Icon className="w-3.5 h-3.5" />}{f.label}</span>
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-32">
          <div className="animate-spin h-6 w-6 border-2 border-emerald-500 border-t-transparent rounded-full" />
        </div>
      ) : versions.length === 0 ? (
        <div className="text-center text-[var(--text-tertiary)] py-12">{t('changelog.noEntries')}</div>
      ) : (
        <>
          <div className="space-y-10">
            {versions.slice((seite - 1) * proSeite, seite * proSeite).map(v => (
              <div key={v.version}>
                {/* Version Header */}
                <div className="flex items-center gap-3 mb-4">
                  <span className="bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 px-3 py-1 rounded-full font-mono text-sm font-semibold">
                    v{v.version}
                  </span>
                  <span className="text-[var(--text-tertiary)] text-sm">{formatDate(v.datum)}</span>
                  <div className="flex-1 border-t border-line" />
                </div>

                {/* Einträge */}
                <div className="space-y-3 pl-2">
                  {v.entries.map(e => {
                    const cfg = TYP_CONFIG[e.typ] || TYP_CONFIG.improvement;
                    return (
                      <div key={e.id} className="flex gap-3 pl-4 border-l-2 border-line hover:border-emerald-400 transition-colors">
                        <div className="flex-1 py-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${cfg.color}`}>
                              <cfg.Icon className="w-3 h-3" /> {cfg.label}
                            </span>
                            <span className="font-medium text-content text-sm">{e.titel}</span>
                            {e.modul && (
                              <span className="text-xs bg-surface-elevated text-[var(--text-tertiary)] px-2 py-0.5 rounded">{e.modul}</span>
                            )}
                          </div>
                          <p className="text-sm text-content-secondary mt-1">{e.beschreibung}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
          {versions.length > proSeite && (() => {
            const gesamtSeiten = Math.ceil(versions.length / proSeite);
            return (
              <div className="flex items-center justify-between mt-6 pt-4 border-t border-[var(--border-table)]">
                <span className="text-sm text-[var(--text-secondary)]">
                  Seite {seite} von {gesamtSeiten}
                </span>
                <div className="flex gap-2">
                  <button
                    onClick={() => setSeite(s => Math.max(1, s - 1))}
                    disabled={seite === 1}
                    className="px-3 py-1.5 text-sm border border-[var(--border-card)] rounded-lg disabled:opacity-40 hover:bg-[var(--bg-inset)]"
                  >
                    &larr; {t('common.back') || 'Zurück'}
                  </button>
                  <button
                    onClick={() => setSeite(s => Math.min(gesamtSeiten, s + 1))}
                    disabled={seite === gesamtSeiten}
                    className="px-3 py-1.5 text-sm border border-[var(--border-card)] rounded-lg disabled:opacity-40 hover:bg-[var(--bg-inset)]"
                  >
                    {t('common.next') || 'Weiter'} &rarr;
                  </button>
                </div>
              </div>
            );
          })()}
        </>
      )}
    </div>
  );
}
